import {
  mapReservationError,
  type CanonicalReservationError,
  type ReservationErrorKind,
  type ReservationErrorPayload,
  type ReservationErrorTranslationKey,
} from './reservationErrorMapper';

export type SubmitErrorKind = ReservationErrorKind;

export type SubmitError = {
  userTitle: string;
  userMessage: string;
  debugId: string | null;
  kind: SubmitErrorKind;
  translationKey: ReservationErrorTranslationKey;
  canonicalError: CanonicalReservationError;
};

const RETRYABLE_RESERVATION_ERROR_KINDS = new Set<SubmitErrorKind>([
  'network',
  'offline',
  'backend_unavailable',
  'server',
  'unknown',
]);

export const isRetryableReservationError = (kind: SubmitErrorKind): boolean =>
  RETRYABLE_RESERVATION_ERROR_KINDS.has(kind);

export const shouldShowReservationErrorDebugId = (kind: SubmitErrorKind): boolean =>
  isRetryableReservationError(kind);

const messages: Record<
  'hu' | 'en',
  Record<SubmitErrorKind, { title: string; message: string }>
> = {
  hu: {
    network: {
      title: 'Nem sikerült csatlakozni',
      message: 'Nincs internetkapcsolat vagy a szerver nem elérhető. Próbáld újra.',
    },
    offline: {
      title: 'Nincs internetkapcsolat',
      message: 'Úgy tűnik, offline vagy. Ellenőrizd az internetkapcsolatot, majd próbáld újra.',
    },
    backend_unavailable: {
      title: 'A foglalási rendszer átmenetileg nem elérhető',
      message: 'A szerver most nem válaszol. Próbáld újra később.',
    },
    rate_limit: {
      title: 'Túl sok próbálkozás',
      message:
        'Túl sok próbálkozás rövid idő alatt. Várj egy kicsit és próbáld újra.',
    },
    validation: {
      title: 'Hibás adatok',
      message: 'Néhány adat hibás vagy hiányzik. Ellenőrizd a mezőket.',
    },
    required_field: {
      title: 'Hiányzó adat',
      message: 'Néhány kötelező adat hiányzik. Ellenőrizd a mezőket.',
    },
    invalid_email: {
      title: 'Hibás e-mail cím',
      message: 'Az e-mail cím hibás vagy hiányzik. Ellenőrizd a mezőt.',
    },
    invalid_phone: {
      title: 'Hibás telefonszám',
      message: 'A telefonszám hibás vagy hiányzik. Ellenőrizd a mezőt.',
    },
    invalid_headcount: {
      title: 'Hibás létszám',
      message: 'A megadott létszám nem fogadható el. Ellenőrizd a mezőt.',
    },
    booking_window: {
      title: 'Nem foglalható időpont',
      message:
        'A foglalás kezdése vagy befejezése kívül esik a foglalható időszakon. Válassz másik időpontot.',
    },
    capacity: {
      title: 'Nincs kapacitás',
      message: 'Erre az időpontra nincs elég szabad hely. Válassz másik időpontot.',
    },
    reservation_state: {
      title: 'A foglalás nem módosítható',
      message: 'Ez a foglalás jelenleg nem kezelhető ezzel a művelettel.',
    },
    permission: {
      title: 'Nincs jogosultság',
      message: 'Nincs jogosultságod ehhez a foglalási művelethez.',
    },
    token_invalid: {
      title: 'Érvénytelen foglalási link',
      message: 'A foglalási link érvénytelen. Ellenőrizd, hogy a teljes linket nyitottad-e meg.',
    },
    token_expired: {
      title: 'Lejárt foglalási link',
      message: 'A foglalási link lejárt. Régi linkkel ez a foglalás már nem kezelhető online.',
    },
    server: {
      title: 'Szerverhiba',
      message: 'Szerverhiba történt. Próbáld újra később.',
    },
    unknown: {
      title: 'Ismeretlen hiba',
      message: 'Ismeretlen hiba történt. Próbáld újra.',
    },
  },
  en: {
    network: {
      title: 'Connection failed',
      message: 'No internet connection or the server is unavailable. Please try again.',
    },
    offline: {
      title: 'No internet connection',
      message: 'You appear to be offline. Check your connection and try again.',
    },
    backend_unavailable: {
      title: 'Reservation system unavailable',
      message: 'The server is not responding right now. Please try again later.',
    },
    rate_limit: {
      title: 'Too many attempts',
      message: 'Too many attempts in a short time. Wait a bit and try again.',
    },
    validation: {
      title: 'Invalid details',
      message: 'Some details are missing or invalid. Please check the fields.',
    },
    required_field: {
      title: 'Missing details',
      message: 'Some required details are missing. Please check the fields.',
    },
    invalid_email: {
      title: 'Invalid email address',
      message: 'The email address is missing or invalid. Please check the field.',
    },
    invalid_phone: {
      title: 'Invalid phone number',
      message: 'The phone number is missing or invalid. Please check the field.',
    },
    invalid_headcount: {
      title: 'Invalid party size',
      message: 'The party size cannot be accepted. Please check the field.',
    },
    booking_window: {
      title: 'Time not available',
      message:
        'The reservation starts or ends outside the available booking period. Please choose another time.',
    },
    capacity: {
      title: 'No capacity',
      message: 'There are not enough seats available for this time. Please choose another time.',
    },
    reservation_state: {
      title: 'Reservation cannot be changed',
      message: 'This reservation cannot currently be handled with this action.',
    },
    permission: {
      title: 'Permission denied',
      message: 'You do not have permission for this reservation action.',
    },
    token_invalid: {
      title: 'Invalid reservation link',
      message: 'The reservation link is invalid. Check that you opened the complete link.',
    },
    token_expired: {
      title: 'Expired reservation link',
      message: 'The reservation link has expired. This booking can no longer be managed online with this old link.',
    },
    server: {
      title: 'Server error',
      message: 'A server error occurred. Please try again later.',
    },
    unknown: {
      title: 'Unknown error',
      message: 'An unknown error occurred. Please try again.',
    },
  },
};

const translationKeyMessages: Partial<
  Record<
    ReservationErrorTranslationKey,
    Record<'hu' | 'en', { title: string; message: string }>
  >
> = {
  'reservation.errors.capacity': {
    hu: {
      title: 'Erre az időpontra nincs elég szabad hely',
      message: 'Erre az időpontra nincs elég szabad hely. Válassz másik időpontot.',
    },
    en: {
      title: 'Not enough seats available',
      message: 'There are not enough seats available at this time. Please choose another time.',
    },
  },
  'reservation.errors.reservation_not_found': {
    hu: {
      title: 'A foglalás nem található',
      message: 'Ez a foglalás már nem található. Ellenőrizd, hogy a megfelelő linket nyitottad-e meg.',
    },
    en: {
      title: 'Reservation not found',
      message: 'This reservation could not be found. Check that you opened the correct link.',
    },
  },
  'reservation.errors.reservation_not_modifiable': {
    hu: {
      title: 'A foglalás nem módosítható',
      message: 'Ez a foglalás jelenleg nem módosítható. Ha segítségre van szükséged, keresd az éttermet.',
    },
    en: {
      title: 'Reservation cannot be modified',
      message: 'This reservation cannot be modified right now. If you need help, please contact the restaurant.',
    },
  },
  'reservation.errors.past_reservation': {
    hu: {
      title: 'Múltbeli foglalás',
      message: 'Múltbeli foglalás már nem módosítható online.',
    },
    en: {
      title: 'Past reservation',
      message: 'Past reservations can no longer be modified online.',
    },
  },
  'reservation.errors.reservation_not_available': {
    hu: {
      title: 'Nem foglalható időpont',
      message: 'Ez az időpont jelenleg nem foglalható. Válassz másik időpontot.',
    },
    en: {
      title: 'Time not available',
      message: 'This time is not available for reservation right now. Please choose another time.',
    },
  },
  'reservation.errors.booking_window': {
    hu: {
      title: 'Nem foglalható időpont',
      message: 'A foglalás kezdése vagy befejezése kívül esik a foglalható időszakon. Válassz másik időpontot.',
    },
    en: {
      title: 'Time not available',
      message: 'The reservation starts or ends outside the available booking period. Please choose another time.',
    },
  },
  'reservation.errors.unknown': {
    hu: {
      title: 'Nem sikerült feldolgozni a kérést',
      message: 'Nem tudtuk feldolgozni a kérést. Próbáld újra, vagy vedd fel a kapcsolatot az étteremmel.',
    },
    en: {
      title: 'Could not process the request',
      message: 'We could not process the request. Please try again, or contact the restaurant directly.',
    },
  },
};

const resolveSubmitErrorCopy = (
  canonicalError: CanonicalReservationError,
  locale: 'hu' | 'en'
) => {
  if ([canonicalError.metadata.reasonCode, canonicalError.metadata.backendCode,
       canonicalError.metadata.backendError].some(code => code?.toUpperCase() === 'RESERVATION_DURATION_EXCEEDED')) {
    return locale === 'hu'
      ? { title: 'Túl hosszú foglalás', message: 'A foglalás időtartamának 24 óránál rövidebbnek kell lennie. Ellenőrizd a kezdés és a befejezés dátumát.' }
      : { title: 'Reservation too long', message: 'The reservation must be shorter than 24 hours. Check the start and end dates.' };
  }
  if (canonicalError.translationKey === 'reservation.errors.capacity') {
    const remaining =
      canonicalError.metadata.remaining ?? canonicalError.metadata.capacityRemaining;
    if (typeof remaining === 'number' && Number.isFinite(remaining)) {
      return locale === 'hu'
        ? {
            title: 'Erre az időpontra nincs elég szabad hely',
            message: `Ebben az idősávban már csak ${Math.max(0, remaining)} fő számára van szabad kapacitás. Válassz másik időpontot vagy csökkentsd a létszámot.`,
          }
        : {
            title: 'Not enough seats available',
            message: `Only ${Math.max(0, remaining)} seats are available in this time window. Please choose another time or reduce the party size.`,
          };
    }
  }
  if (canonicalError.translationKey === 'reservation.errors.booking_window') {
    const { bookingStart, bookingEnd } = canonicalError.metadata;
    if (bookingStart && bookingEnd) {
      return locale === 'hu'
        ? {
            title: 'Nem foglalható időpont',
            message: `A foglalás teljes időtartamának ${bookingStart} és ${bookingEnd} közé kell esnie.`,
          }
        : {
            title: 'Time not available',
            message: `The full reservation duration must fit between ${bookingStart} and ${bookingEnd}.`,
          };
    }
  }

  return translationKeyMessages[canonicalError.translationKey]?.[locale]
    ?? messages[locale][canonicalError.kind];
};

export const normalizeSubmitError = ({
  response,
  payload,
  error,
  isOnline,
  traceId,
  locale = 'hu',
}: {
  response?: Response;
  payload?: ReservationErrorPayload;
  error?: unknown;
  isOnline?: boolean;
  traceId?: string | null;
  locale?: 'hu' | 'en';
}): SubmitError => {
  const canonicalError = mapReservationError({ response, payload, error, isOnline });
  const { title, message } = resolveSubmitErrorCopy(canonicalError, locale);

  return {
    kind: canonicalError.kind,
    translationKey: canonicalError.translationKey,
    canonicalError,
    userTitle: title,
    userMessage: message,
    debugId: traceId ?? null,
  };
};
